import { FunctionComponent, useMemo, useEffect, useRef } from "react";
import { isRouteErrorResponse, useRouteError, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Home } from "lucide-react";
import "./ErrorPage.scss";

interface ErrorPageProps {
  status?: number;
  message?: string;
  title?: string;
}

// ── Floating particle component ──────────────────────────────────────────────
const FloatingEmoji = ({
  emoji,
  style,
  delay = 0,
}: {
  emoji: string;
  style: React.CSSProperties;
  delay?: number;
}) => (
  <motion.span
    className="floating-emoji"
    style={style}
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: [0, 1, 1, 0], y: [40, 0, -20, -60] }}
    transition={{
      duration: 6,
      delay,
      repeat: Infinity,
      repeatDelay: Math.random() * 4 + 2,
      ease: "easeInOut",
    }}
  >
    {emoji}
  </motion.span>
);

// ── 404 – Lost Student illustration ─────────────────────────────────────────
const Lost404Animation = () => (
  <div className="edu-scene scene-404">
    {/* Blackboard */}
    <motion.div
      className="blackboard"
      initial={{ rotateY: -15, opacity: 0 }}
      animate={{ rotateY: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <div className="board-frame">
        <div className="board-surface">
          <span className="board-number">404</span>
          <motion.div
            className="chalk-line"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
          />
          <span className="board-subtext">Not Found</span>
          {/* Chalk dust */}
          {["💨", "✏️", "📝"].map((e, i) => (
            <FloatingEmoji
              key={i}
              emoji={e}
              style={{ top: `${30 + i * 20}%`, left: `${10 + i * 25}%` }}
              delay={i * 0.8}
            />
          ))}
        </div>
      </div>
      {/* Chalk tray */}
      <div className="chalk-tray">
        <span className="chalk-piece" />
        <span className="chalk-piece" style={{ width: "2rem", opacity: 0.7 }} />
        <span className="chalk-piece" style={{ width: "1.4rem", opacity: 0.5 }} />
      </div>
    </motion.div>

    {/* Floating book stack */}
    <motion.div
      className="book-stack"
      animate={{ y: [-6, 6, -6] }}
      transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
    >
      <div className="book book-blue" />
      <div className="book book-red" />
      <div className="book book-yellow" />
    </motion.div>

    {/* Question marks orbiting */}
    {["?", "?", "?"].map((q, i) => (
      <motion.span
        key={i}
        className="orbit-q"
        style={{
          transformOrigin: "80px 80px",
          rotate: i * 120,
        }}
        animate={{ rotate: [i * 120, i * 120 + 360] }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      >
        {q}
      </motion.span>
    ))}
  </div>
);

// ── 500 – Server Crash Lab illustration ──────────────────────────────────────
const ServerError500Animation = () => (
  <div className="edu-scene scene-500">
    {/* Computer / monitor */}
    <motion.div
      className="edu-monitor"
      initial={{ scale: 0.7, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="monitor-screen">
        <motion.div
          className="screen-glitch"
          animate={{ opacity: [1, 0.2, 1, 0, 1], x: [0, -4, 4, -2, 0] }}
          transition={{ duration: 0.3, repeat: Infinity, repeatDelay: 2 }}
        >
          <span className="glitch-text">SYSTEM ERROR</span>
          <div className="error-lines">
            <div className="err-line red" />
            <div className="err-line orange" />
            <div className="err-line red short" />
          </div>
        </motion.div>
      </div>
      <div className="monitor-stand" />
      <div className="monitor-base" />
    </motion.div>

    {/* Smoke puffs */}
    {[0, 1, 2].map((i) => (
      <motion.div
        key={i}
        className="smoke-puff"
        style={{ left: `${35 + i * 10}%` }}
        animate={{ y: [0, -60], opacity: [0.8, 0], scale: [0.5, 1.5] }}
        transition={{
          duration: 2,
          delay: i * 0.6,
          repeat: Infinity,
          repeatDelay: 1,
        }}
      />
    ))}

    {/* Flying warning signs */}
    {["⚠️", "🔧", "💥"].map((e, i) => (
      <FloatingEmoji
        key={i}
        emoji={e}
        style={{ top: `${10 + i * 15}%`, right: `${5 + i * 8}%` }}
        delay={i * 1.2}
      />
    ))}
  </div>
);

// ── 403/401 – Access Denied illustration ────────────────────────────────────
const AccessDeniedAnimation = () => (
  <div className="edu-scene scene-403">
    {/* Padlock */}
    <motion.div
      className="edu-padlock"
      initial={{ scale: 0, rotate: -20 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ type: "spring", stiffness: 200, damping: 14 }}
    >
      <motion.div
        className="lock-shackle"
        animate={{ rotateX: [0, 20, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
      />
      <div className="lock-body">
        <motion.div
          className="keyhole"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1, repeat: Infinity, repeatDelay: 1.5 }}
        />
      </div>
    </motion.div>

    {/* NO ACCESS badge */}
    <motion.div
      className="no-access-badge"
      initial={{ x: 40, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ delay: 0.5 }}
    >
      🚫 ACCESS DENIED
    </motion.div>

    {/* Floating keys that don't fit */}
    {["🔑", "🗝️"].map((e, i) => (
      <motion.span
        key={i}
        className="flying-key"
        style={{ top: `${20 + i * 30}%`, left: `${10 + i * 60}%` }}
        animate={{ x: [0, 15, -10, 0], rotate: [0, 20, -10, 0] }}
        transition={{ duration: 3 + i, repeat: Infinity, ease: "easeInOut" }}
      >
        {e}
      </motion.span>
    ))}
  </div>
);

// ── 200 / No Content – Empty Desk illustration ───────────────────────────────
const EmptyDeskAnimation = () => (
  <div className="edu-scene scene-200">
    <motion.div
      className="empty-desk"
      initial={{ y: 30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7 }}
    >
      {/* Desk surface */}
      <div className="desk-surface">
        <motion.div
          className="desk-paper"
          animate={{ rotate: [-1, 1, -1] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="paper-line" />
          <div className="paper-line short" />
          <div className="paper-line" />
        </motion.div>
        {/* Empty coffee cup */}
        <div className="desk-cup">
          <motion.div
            className="cup-steam"
            animate={{ y: [0, -12], opacity: [1, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </div>
      </div>
      <div className="desk-leg left-leg" />
      <div className="desk-leg right-leg" />
    </motion.div>

    {/* Floating education emojis */}
    {["📚", "📖", "🎓", "✏️"].map((e, i) => (
      <FloatingEmoji
        key={i}
        emoji={e}
        style={{ top: `${5 + i * 15}%`, left: `${5 + i * 22}%` }}
        delay={i * 0.7}
      />
    ))}

    <motion.p
      className="empty-label"
      animate={{ opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 2.5, repeat: Infinity }}
    >
      Nothing to see here...
    </motion.p>
  </div>
);

// ── Default – Generic Education Error ────────────────────────────────────────
const DefaultAnimation = () => (
  <div className="edu-scene scene-default">
    <motion.div
      className="graduation-cap"
      animate={{ y: [-8, 8, -8], rotate: [-5, 5, -5] }}
      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
    >
      🎓
    </motion.div>
    {["📘", "📗", "📕", "📙"].map((e, i) => (
      <motion.span
        key={i}
        className="orbit-book"
        style={{ "--i": i } as React.CSSProperties}
        animate={{ rotate: [i * 90, i * 90 + 360] }}
        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
      >
        {e}
      </motion.span>
    ))}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const ErrorPage: FunctionComponent<ErrorPageProps> = ({ status, message, title }) => {
  const error = useRouteError();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Resolve status and messages
  let errorMessage: string = message || "";
  let errorCode: number = status || 500;
  let errorTitle: string = title || "An Error Occurred";

  if (error) {
    if (isRouteErrorResponse(error)) {
      errorMessage = error.data || error.statusText;
      errorCode = error.status;
      errorTitle = `Oops! ${errorCode}`;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === "string") {
      errorMessage = error;
    } else {
      errorMessage = "Unknown error";
    }
  }

  // ── Starfield / particle canvas ───────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: { x: number; y: number; r: number; vx: number; vy: number; alpha: number }[] =
      Array.from({ length: 80 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 1.5 + 0.5,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        alpha: Math.random() * 0.5 + 0.1,
      }));

    let raf: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(180, 200, 255, ${p.alpha})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      });
      raf = requestAnimationFrame(draw);
    };
    draw();

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // ── Config per status ────────────────────────────────────────────────────
  const config = useMemo(() => {
    switch (errorCode) {
      case 404:
      case 4040:
        return {
          title: title || "Page Not Found",
          desc:
            errorMessage ||
            "Looks like this lesson doesn't exist yet. The page may have been moved or removed.",
          scene: <Lost404Animation />,
          accent: "#6c63ff",
          badge: "404",
          badgeLabel: "Missing Lesson",
        };
      case 500:
        return {
          title: title || "Something Went Wrong",
          desc:
            errorMessage ||
            "Our classroom server hit an unexpected snag. Please try again in a moment.",
          scene: <ServerError500Animation />,
          accent: "#ff6b6b",
          badge: "500",
          badgeLabel: "Server Error",
        };
      case 403:
      case 401:
        return {
          title: title || "Access Denied",
          desc:
            errorMessage ||
            "You don't have the key to this classroom. Please check your permissions.",
          scene: <AccessDeniedAnimation />,
          accent: "#ffa94d",
          badge: errorCode === 401 ? "401" : "403",
          badgeLabel: "Unauthorised",
        };
      case 200:
        return {
          title: title || "No Content Found",
          desc:
            errorMessage ||
            "The desk is empty — there's no content to display right now.",
          scene: <EmptyDeskAnimation />,
          accent: "#38d9a9",
          badge: "200",
          badgeLabel: "Empty",
        };
      default:
        return {
          title: errorTitle,
          desc: errorMessage || "An unexpected error occurred in the classroom.",
          scene: <DefaultAnimation />,
          accent: "#a78bfa",
          badge: String(errorCode),
          badgeLabel: "Error",
        };
    }
  }, [errorCode, errorMessage, errorTitle, title]);

  return (
    <div className="ep-wrapper">
      {/* Particle canvas */}
      <canvas ref={canvasRef} className="ep-canvas" />

      {/* Decorative grid */}
      <div className="ep-grid" />

      {/* Main card */}
      <AnimatePresence>
        <motion.div
          className="ep-card"
          initial={{ opacity: 0, y: 60, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={{ "--accent": config.accent } as React.CSSProperties}
        >
          {/* Top accent bar */}
          <motion.div
            className="ep-accent-bar"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />

          {/* Status badge */}
          <motion.div
            className="ep-badge"
            initial={{ scale: 0, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.1 }}
          >
            <span className="badge-code">{config.badge}</span>
            <span className="badge-label">{config.badgeLabel}</span>
          </motion.div>

          {/* Scene illustration */}
          <motion.div
            className="ep-scene-wrapper"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {config.scene}
          </motion.div>

          {/* Text */}
          <motion.h1
            className="ep-title"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.5 }}
          >
            {config.title}
          </motion.h1>

          <motion.p
            className="ep-desc"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.45, duration: 0.5 }}
          >
            {config.desc}
          </motion.p>

          {/* Action buttons */}
          <motion.div
            className="ep-actions"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.55, duration: 0.5 }}
          >
            <motion.button
              className="ep-btn ep-btn-ghost"
              onClick={() => navigate(-1)}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <ArrowLeft size={17} />
              Go Back
            </motion.button>

            <motion.button
              className="ep-btn ep-btn-primary"
              onClick={() => navigate("/home")}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              <Home size={17} />
              Back to Home
            </motion.button>
          </motion.div>

          {/* Corner decoration – pencil */}
          <div className="ep-corner-deco">✏️</div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ErrorPage;
